import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const MARGIN = 8
const GAP = 8
const SHOW_DELAY_MS = 250

type Tip = { text: string; anchor: HTMLElement }

/**
 * Styled replacement for the browser's white `title` box: put `data-tip="…"` on any element.
 * One delegated listener for the whole app, rendered in a portal (never clipped by
 * overflow:hidden menus), same look as {@link ViewportTooltip}. Mouse hover and keyboard
 * focus only — touch screens get nothing, there is no hover there.
 */
export function GlobalTooltip(): React.ReactElement | null {
  const [tip, setTip] = useState<Tip | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; maxWidth: number } | null>(null)
  const tipRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (typeof document === 'undefined') return
    let timer: number | null = null
    let current: HTMLElement | null = null

    const clearTimer = () => {
      if (timer !== null) window.clearTimeout(timer)
      timer = null
    }
    const hide = () => {
      clearTimer()
      current = null
      setTip(null)
    }
    const anchorOf = (target: EventTarget | null): HTMLElement | null =>
      target instanceof Element ? (target.closest('[data-tip]') as HTMLElement | null) : null
    const show = (el: HTMLElement, delay: number) => {
      const text = (el.getAttribute('data-tip') ?? '').trim()
      if (!text) return
      clearTimer()
      current = el
      setTip(null)
      timer = window.setTimeout(() => {
        timer = null
        if (current === el && el.isConnected) setTip({ text, anchor: el })
      }, delay)
    }

    const onPointerOver = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      const el = anchorOf(e.target)
      if (el === current) return
      if (!el) {
        hide()
        return
      }
      show(el, SHOW_DELAY_MS)
    }
    const onPointerOut = (e: PointerEvent) => {
      // cursor left the window
      if (e.pointerType === 'mouse' && !e.relatedTarget) hide()
    }
    const onFocusIn = (e: FocusEvent) => {
      const el = anchorOf(e.target)
      if (!el) return
      let keyboard = false
      try {
        keyboard = (e.target as Element).matches(':focus-visible')
      } catch {
        keyboard = false
      }
      if (keyboard) show(el, 0)
    }

    document.addEventListener('pointerover', onPointerOver)
    document.addEventListener('pointerout', onPointerOut)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', hide)
    document.addEventListener('pointerdown', hide, true)
    document.addEventListener('keydown', hide, true)
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    window.addEventListener('blur', hide)
    return () => {
      clearTimer()
      document.removeEventListener('pointerover', onPointerOver)
      document.removeEventListener('pointerout', onPointerOut)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', hide)
      document.removeEventListener('pointerdown', hide, true)
      document.removeEventListener('keydown', hide, true)
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
      window.removeEventListener('blur', hide)
    }
  }, [])

  useLayoutEffect(() => {
    setPos(null)
    if (!tip) return
    const el = tipRef.current
    const a = tip.anchor
    if (!el || !a.isConnected) return
    const r = a.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const maxWidth = Math.min(320, vw - MARGIN * 2)
    el.style.maxWidth = `${maxWidth}px`
    const tw = el.offsetWidth
    const th = el.offsetHeight
    let left = r.left + r.width / 2 - tw / 2
    left = Math.max(MARGIN, Math.min(left, vw - MARGIN - tw))
    let top = r.top - th - GAP
    if (top < MARGIN) top = r.bottom + GAP
    top = Math.max(MARGIN, Math.min(top, vh - MARGIN - th))
    setPos({ top, left, maxWidth })
  }, [tip])

  if (!tip || typeof document === 'undefined') return null
  return createPortal(
    <span
      ref={tipRef}
      className="team-presence-tooltip-text team-presence-tooltip-text--viewport app-tooltip"
      role="tooltip"
      style={{
        position: 'fixed',
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        maxWidth: pos?.maxWidth,
        visibility: pos ? 'visible' : 'hidden',
        pointerEvents: 'none',
        zIndex: 10000,
      }}
    >
      {tip.text}
    </span>,
    document.body,
  )
}
